import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, RefreshCw, Plus, Trash2, Save } from "lucide-react";
import api from "@/lib/api";
import { apiErr } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import WorkerPicker from "@/components/op/WorkerPicker";
import SearchSelect from "@/components/op/SearchSelect";
import OpHeader from "@/components/op/OpHeader";

const num = (n) => new Intl.NumberFormat("id-ID").format(n || 0);

function CatatRijek() {
  const [worker, setWorker] = useState(null);
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([{ product_id: "", quantity: "", keterangan: "" }]);
  const [saving, setSaving] = useState(false);
  const idemRef = useRef(crypto.randomUUID());

  useEffect(() => { api.get("/op/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  const setItem = (i, k, v) => setItems((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const save = async () => {
    if (!worker) return toast.error("Pilih pengepul dulu");
    const v = [];
    for (const it of items) {
      if (!it.product_id || !(Number(it.quantity) > 0)) continue;
      if (!String(it.keterangan).trim()) return toast.error("Keterangan rijek wajib diisi");
      v.push({ product_id: it.product_id, quantity: Number(it.quantity), repairable: it.repairable !== false, keterangan: it.keterangan });
    }
    if (!v.length) return toast.error("Isi minimal satu barang rijek");
    setSaving(true);
    try {
      const { data } = await api.post("/op/rijek-susulan", { worker_id: worker.worker_id, items: v, idempotency_key: idemRef.current });
      toast.success(`Rijek susulan tercatat · ${data.op_no} · siap dikirim ke perbaikan`);
      idemRef.current = crypto.randomUUID();
      setItems([{ product_id: "", quantity: "", keterangan: "" }]); setWorker(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-5" data-testid="op-rijek-catat">
      <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">
        Catat rijek yang <b>baru ditemukan setelah setor</b>. Jumlah ini akan mengurangi barang bagus & ongkos setoran
        pengepul, dan otomatis masuk ke daftar <b>Kirim Perbaikan</b>.
      </div>
      <WorkerPicker value={worker} onChange={setWorker} />
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`op-rijeks-row-${i}`}>
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-8">
                <Label className="text-[11px]">Barang</Label>
                <SearchSelect value={it.product_id} onValueChange={(v) => setItem(i, "product_id", v)}
                  options={products.map((p) => ({ value: p.product_id, label: p.product_name }))}
                  placeholder="Pilih / cari barang" testId={`op-rijeks-product-${i}`} />
              </div>
              <div className="col-span-3"><Label className="text-[11px]">Jml Rijek</Label><Input type="number" inputMode="numeric" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="h-10" data-testid={`op-rijeks-qty-${i}`} /></div>
              <div className="col-span-1"><button onClick={() => setItems((a) => a.filter((_, j) => j !== i))} className="p-2 text-rose-500 mt-5" data-testid={`op-rijeks-del-${i}`}><Trash2 className="w-4 h-4" /></button></div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex rounded-lg bg-slate-100 p-0.5 text-[11px] font-semibold shrink-0" data-testid={`op-rijeks-type-${i}`}>
                <button type="button" onClick={() => setItem(i, "repairable", true)} className={`px-2 py-1 rounded-md ${it.repairable !== false ? "bg-white shadow text-emerald-700" : "text-slate-500"}`} data-testid={`op-rijeks-rep-${i}`}>Bisa diperbaiki</button>
                <button type="button" onClick={() => setItem(i, "repairable", false)} className={`px-2 py-1 rounded-md ${it.repairable === false ? "bg-white shadow text-rose-700" : "text-slate-500"}`} data-testid={`op-rijeks-afkir-${i}`}>Afkir</button>
              </div>
              <Input placeholder="Keterangan rijek (wajib)" value={it.keterangan} onChange={(e) => setItem(i, "keterangan", e.target.value)} className="h-9" data-testid={`op-rijeks-note-${i}`} />
            </div>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" className="rounded-full" onClick={() => setItems((a) => [...a, { product_id: "", quantity: "", keterangan: "" }])} data-testid="op-rijeks-add"><Plus className="w-4 h-4 mr-1" />Tambah Barang</Button>
      <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-sm">
        <div className="text-slate-500">Total Rijek <b className="text-orange-600">{num(items.reduce((a, x) => a + Number(x.quantity || 0), 0))}</b> PCS</div>
        <Button disabled={saving} onClick={save} className="rounded-full bg-orange-600 hover:bg-orange-700" data-testid="op-rijeks-save"><Save className="w-4 h-4 mr-1.5" />Catat Rijek</Button>
      </div>
    </div>
  );
}

export default function Rijek() {
  const [tab, setTab] = useState("CATAT");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [fisik, setFisik] = useState({});
  const [bagus, setBagus] = useState({});
  const [notes, setNotes] = useState({});
  const keys = useRef({});
  const keyFor = (k) => (keys.current[k] ||= crypto.randomUUID());

  const load = useCallback(() => {
    if (tab === "CATAT") return;
    setLoading(true);
    const status = tab === "KIRIM" ? "MENUNGGU_DIAMBIL" : "DIAMBIL";
    api.get(`/op/rijek?status=${status}`).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const kirim = async (rid) => {
    setBusy(rid);
    try { await api.post("/op/rijek/kirim", { repair_id: rid, idempotency_key: keyFor(`kirim:${rid}`) }); delete keys.current[`kirim:${rid}`]; toast.success("Rijek dikirim ke perbaikan"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const terima = async (r) => {
    const rid = r.repair_id;
    const at = r.at_tailor ?? r.outstanding ?? r.qty;
    const f = Number(fisik[rid] || 0);
    const g = Number(bagus[rid] || 0);
    if (!(f > 0)) return toast.error("Isi Jumlah Kembali (Fisik) dulu");
    if (!(g >= 0 && g <= f && f <= at)) return toast.error(`Isian tidak valid: 0 ≤ bagus ≤ kembali fisik ≤ sisa di penjahit (${at})`);
    const masih = f - g;
    if (masih > 0 && !(notes[rid] || "").trim()) return toast.error("Keterangan wajib diisi bila masih ada rijek");
    setBusy(rid);
    try {
      await api.post("/op/rijek/terima", {
        repair_id: rid, returned_qty: f, good_qty: g,
        notes: notes[rid] || null, idempotency_key: keyFor(`terima:${rid}`),
      });
      delete keys.current[`terima:${rid}`];
      setFisik((s) => ({ ...s, [rid]: "" })); setBagus((s) => ({ ...s, [rid]: "" })); setNotes((s) => ({ ...s, [rid]: "" }));
      toast.success("Perbaikan diterima");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  return (
    <div className="w-full pb-10" data-testid="op-rijek">
      <OpHeader title="RIJEK & PERBAIKAN" />
      <div className="flex items-center justify-between mb-4">
        <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="op-rijek-tabs">
          {["CATAT", "KIRIM", "TERIMA"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`op-rijek-tab-${t}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${tab === t ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
              {t === "CATAT" ? "Catat Rijek" : t === "KIRIM" ? "Kirim Perbaikan" : "Terima Perbaikan"}
            </button>
          ))}
        </div>
        {tab !== "CATAT" && <button onClick={load} className="p-2 rounded-full hover:bg-slate-100 text-slate-500" data-testid="op-rijek-refresh"><RefreshCw className="w-4 h-4" /></button>}
      </div>

      {tab === "CATAT" ? <CatatRijek /> : loading ? <div className="text-slate-400 text-sm">Memuat…</div> : rows.length === 0 ? (
        <div className="text-slate-400 text-sm bg-white rounded-xl border border-slate-200 p-6 text-center" data-testid="op-rijek-empty">
          {tab === "KIRIM" ? "Tidak ada rijek menunggu dikirim." : "Tidak ada rijek yang sedang diperbaiki."}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const rid = r.repair_id;
            const at = r.at_tailor ?? r.outstanding ?? r.qty;
            const f = Number(fisik[rid] || 0);
            const g = Number(bagus[rid] || 0);
            const masih = Math.max(f - g, 0);
            const sisaPenjahit = at - f;
            return (
              <div key={rid} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`op-rijek-row-${rid}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate"><span className="font-mono text-blue-700">{r.worker_id}</span> · {r.worker_name}</div>
                    <div className="text-xs text-slate-500 truncate">{r.product_name || r.product_id} · {r.source_date}</div>
                    {tab === "KIRIM" ? (
                      <div className="text-xs text-slate-600 mt-0.5">Menunggu dikirim: <b data-testid={`op-rijek-at-${rid}`}>{num(r.waiting ?? r.outstanding ?? r.qty)}</b> dari {num(r.qty)} PCS</div>
                    ) : (
                      <div className="text-xs text-slate-600 mt-0.5">Sisa di penjahit: <b data-testid={`op-rijek-at-${rid}`}>{num(at)}</b> dari {num(r.qty)} PCS</div>
                    )}
                  </div>
                  {tab === "KIRIM" && (
                    <Button size="sm" disabled={busy === rid} onClick={() => kirim(rid)} className="rounded-full bg-orange-600 hover:bg-orange-700 shrink-0" data-testid={`op-rijek-kirim-${rid}`}><Send className="w-4 h-4 mr-1" />Kirim</Button>
                  )}
                </div>

                {tab === "TERIMA" && (
                  <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[11px]">Jumlah Kembali (Fisik)</Label>
                        <Input type="number" inputMode="numeric" min={0} max={at} placeholder="0" value={fisik[rid] || ""}
                          onChange={(e) => setFisik((q) => ({ ...q, [rid]: e.target.value }))} className="h-10" data-testid={`op-rijek-fisik-${rid}`} />
                      </div>
                      <div>
                        <Label className="text-[11px]">Diterima Bagus</Label>
                        <Input type="number" inputMode="numeric" min={0} max={f || at} placeholder="0" value={bagus[rid] || ""}
                          onChange={(e) => setBagus((q) => ({ ...q, [rid]: e.target.value }))} className="h-10" data-testid={`op-rijek-bagus-${rid}`} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-lg bg-orange-50 border border-orange-100 px-3 py-2">Masih Rijek<br /><b className="text-orange-700 text-sm" data-testid={`op-rijek-masih-${rid}`}>{num(masih)} PCS</b></div>
                      <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">Sisa di Penjahit (setelah)<br /><b className="text-slate-700 text-sm" data-testid={`op-rijek-sisa-${rid}`}>{num(sisaPenjahit)} PCS</b></div>
                    </div>
                    {masih > 0 && (
                      <Input placeholder="Keterangan (wajib, karena masih ada rijek)" value={notes[rid] || ""}
                        onChange={(e) => setNotes((n) => ({ ...n, [rid]: e.target.value }))} className="h-9" data-testid={`op-rijek-note-${rid}`} />
                    )}
                    <Button size="sm" disabled={busy === rid} onClick={() => terima(r)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto" data-testid={`op-rijek-terima-${rid}`}><CheckCircle2 className="w-4 h-4 mr-1" />Terima</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
